import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type ApolloClient from 'apollo-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { createAtlAttributionHeader } from '../../utils';

interface UserQueryData {
	user: UserData;
}

export interface UserData {
	picture: string;
	accountId: string | undefined;
	name: string;
}

interface UserQueryVariables {
	accountId: string;
}

export const userQuery = gql`
	query forge_ui_userQuery($accountId: ID!) {
		user(accountId: $accountId) {
			name
			picture
		}
	}
`;

interface UsersQueryData {
	users: [UserData];
}

interface UsersQueryVariables {
	accountIds: string[];
}

export const usersQuery = gql`
	query forge_ui_usersQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			name
			accountId
			picture
		}
	}
`;

export const useUsers = (accountIds: string[], client?: ApolloClient<any>) => {
	const nonEmptyAccountIds = accountIds.filter((id) => !(id === ''));

	const headers: Record<string, string> = {};
	// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
	if (fg('forge-ui-include-atl-attribution-header')) {
		headers['atl-attribution'] = createAtlAttributionHeader();
	}

	const { data } = useQuery<UsersQueryData, UsersQueryVariables>(usersQuery, {
		variables: {
			accountIds: nonEmptyAccountIds,
		},
		client,
		context: {
			headers: headers,
		},
	});

	let users: { [accountId: string]: UserData } = {};

	if (data && data.users) {
		users = data.users.reduce<{
			[accountId: string]: UserData;
		}>((prev, user) => {
			if (user.accountId) {
				prev[user.accountId] = {
					picture: user.picture,
					accountId: user.accountId,
					name: user.name,
				};
			}
			return prev;
		}, {});
	}

	return users;
};

export const useUser = (accountId: string, client?: ApolloClient<any>) => {
	const headers: Record<string, string> = {};
	// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
	if (fg('forge-ui-include-atl-attribution-header')) {
		headers['atl-attribution'] = createAtlAttributionHeader();
	}

	const { data } = useQuery<UserQueryData, UserQueryVariables>(userQuery, {
		variables: {
			accountId,
		},
		skip: !accountId,
		client,
		context: {
			headers: headers,
		},
	});

	if (data && data.user) {
		const { picture, name } = data.user;
		return {
			name,
			picture,
		};
	}

	return {
		name: '',
		picture: '',
	};
};
